import type { SeattleGameState, SeattlePlayer } from '../../types';
import { debug } from '../../utils/debug';
import { SEATTLE_RULES } from '../../../data/map/seattle_metro/config';

/**
 * Seattle Metro Solo ゲーム終了処理
 *
 * 10ターン終了時に呼び出され、清算・得点計算・勝敗判定を行います。
 *
 * @see docs/specifications/logic/seattle_metro_solo/game_end_seattle.md
 */
export function endGameSeattle(
  G: SeattleGameState,
  _ctx: any,
  events: any
): void {
  const player = G.players['0'] as SeattlePlayer;

  debug.log(`[endGameSeattle] Game ending - Turn: ${G.actualTurn}`);

  // 1. 清算処理
  const settlementResult = calculateSettlement(player);

  if (!settlementResult.success) {
    // 清算失敗（支払い不能）→ 敗北
    G.isGameOver = true;
    G.endReason = 'DEFEAT_SETTLEMENT_FAILED';
    player.finalScore = null;
    player.isVictorious = false;
    G.finalScores[player.id] = 0;

    debug.log(`[endGameSeattle] Settlement failed - Cash: $${player.cash}, Required: $${settlementResult.totalCost}`);

    if (events && events.endGame) {
      events.endGame({
        completed: false,
        reason: 'SETTLEMENT_FAILED',
        settlementResult
      });
    }
    return;
  }

  // 2. 現金から清算コストを支払う
  player.cash -= settlementResult.totalCost;

  // 3. 得点計算
  const scoreResult = calculateFinalScore(G, player);

  // 4. 勝敗判定
  const victory = scoreResult.finalScore >= 0;

  // 5. プレイヤー情報更新
  player.finalScore = scoreResult.finalScore;
  player.isVictorious = victory;

  // 6. ゲーム状態更新
  G.isGameOver = true;
  G.endReason = victory ? 'VICTORY' : 'DEFEAT';
  G.finalScores[player.id] = scoreResult.finalScore;

  debug.log(`[endGameSeattle] Game completed - Score: ${scoreResult.finalScore}, Victory: ${victory}`);
  debug.log(`[endGameSeattle] Details: delivered=${scoreResult.deliveredCubes}*5=${scoreResult.deliveredCubesPoints}, remaining=-${scoreResult.remainingGoods}, unconnected=-${scoreResult.unconnectedStations}`);

  // 7. Boardgame.ioにゲーム終了を通知
  if (events && events.endGame) {
    events.endGame({
      completed: true,
      victory,
      settlementResult,
      scoreResult
    });
  }
}

/**
 * 清算コスト計算
 */
export interface SettlementResult {
  shareBuybackCost: number;
  actionUsageCost: number;
  totalCost: number;
  success: boolean;
  engineerUsageCount: number;
  locomotiveUsageCount: number;
  sharesIssued: number;
}

export function calculateSettlement(player: SeattlePlayer): SettlementResult {
  // 株式買い戻しコスト
  const shareBuybackCost = player.sharesIssued * SEATTLE_RULES.shareBuybackPrice;

  // アクション使用料
  const totalActionUsage = player.engineerUsageCount + player.locomotiveUsageCount;
  const actionUsageCost = totalActionUsage * SEATTLE_RULES.actionUsageCost;

  // 合計
  const totalCost = shareBuybackCost + actionUsageCost;

  // 支払い可能かチェック
  const success = player.cash >= totalCost;

  return {
    shareBuybackCost,
    actionUsageCost,
    totalCost,
    success,
    engineerUsageCount: player.engineerUsageCount,
    locomotiveUsageCount: player.locomotiveUsageCount,
    sharesIssued: player.sharesIssued
  };
}

/**
 * 最終得点計算
 */
export interface ScoreResult {
  deliveredCubes: number;
  deliveredCubesPoints: number;
  remainingGoods: number;
  unconnectedStations: number;
  finalScore: number;
}

export function calculateFinalScore(
  G: SeattleGameState,
  player: SeattlePlayer
): ScoreResult {
  // 1. 輸送し取り除いた商品による得点
  const deliveredCubes = player.deliveredCubes;
  const deliveredCubesPoints = deliveredCubes * SEATTLE_RULES.deliveredCubePoints;

  // 2. ボード上に残った商品数（マイナス要因）
  const remainingGoods = countRemainingGoods(G);

  // 3. 未接続駅数（マイナス要因）
  const unconnectedStations = countUnconnectedStations(G);

  // 4. 最終得点
  const finalScore = deliveredCubesPoints - remainingGoods - unconnectedStations;

  return {
    deliveredCubes,
    deliveredCubesPoints,
    remainingGoods,
    unconnectedStations,
    finalScore
  };
}

/**
 * ボード上に残った商品数をカウント
 *
 * Gray City（Downtown/Airport）に留置された商品も hex.goods に含まれるため、
 * 全ての Hex の goods を合計するだけで正しいカウントが得られます。
 */
export function countRemainingGoods(G: SeattleGameState): number {
  let count = 0;
  for (const hex of G.map) {
    if (hex.goods) {
      count += hex.goods.length;
    }
  }
  return count;
}

/**
 * 未接続駅数をカウント
 * Downtown から線路で到達不能な City/Town の数
 */
export function countUnconnectedStations(G: SeattleGameState): number {
  // Downtown から BFS で接続されている駅を列挙
  const connected = new Set<string>();
  const queue: string[] = ['DOWNTOWN'];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (connected.has(current)) continue;
    connected.add(current);

    for (const link of G.completedLinks) {
      if (link.startId === current && !connected.has(link.endId)) {
        queue.push(link.endId);
      }
      if (link.endId === current && !connected.has(link.startId)) {
        queue.push(link.startId);
      }
    }
  }

  // 全 City/Town を取得
  const allStations: string[] = [];
  for (const hex of G.map) {
    if (hex.city?.id) {
      allStations.push(hex.city.id);
    }
    if (hex.town?.id) {
      allStations.push(hex.town.id);
    }
  }

  // 接続されていない駅をカウント
  return allStations.filter(id => !connected.has(id)).length;
}
